import type { CaseStudy } from '../types';

export const container: CaseStudy = {
  id: 'container',
  order: 8,
  title: '集装箱',
  english: 'The Shipping Container',
  category: 'technology',
  era: '1956 — 今',
  region: '美国 → 全球',
  accent: '#367a6a',
  hook: '集装箱的伟大不在于“把货装进箱子”，而在于让船、港口、卡车和铁路第一次能用同一套接口交接。',
  question: '一个铁盒子，为什么能重塑全球贸易、港口城市和产业链？',
  summary:
    '集装箱把装卸从依赖人力的非标劳动，变成可预测、可规模化的标准流程。标准化降低了交接成本，吞吐量带来规模经济，港口、铁路与航运网络又互相加固，最终改变了全球生产布局。',
  thesis: '通用技术的力量，往往来自接口标准与互补基础设施，而不是单件发明本身。',
  quote: '标准不是把所有人变得一样，而是让不同的人可以用低成本的方式交接。',
  entryNodeIds: ['container-standard', 'container-throughput', 'container-network'],
  focusNodeId: 'container-standard',
  lensIds: ['standards', 'network', 'scale', 'scarcity', 'collective-action', 'public-goods'],
  opportunityIds: ['opp-standard-interface'],
  sourceIds: ['levinson-box', 'world-bank-logistics'],
  facts: [
    {
      text: '1956 年，马尔科姆·麦克莱恩的“理想 X 号”从纽瓦克运载集装箱驶往休斯敦，通常被视为现代集装箱航运的开端之一。',
      sourceIds: ['levinson-box'],
    },
    {
      text: '集装箱并非麦克莱恩凭空发明；他的关键贡献是把船、港口、卡车与铁路的交接方式系统化，并推动标准尺寸与专用设备扩散。',
      sourceIds: ['levinson-box'],
    },
    {
      text: '据列文森《The Box》引用的历史估算，美国 1950 年代集装箱化使装卸成本从每吨约 5.86 美元降至约 0.16 美元；该数字依赖具体港口与口径，不宜当作全球通用值。',
      sourceIds: ['levinson-box'],
    },
  ],
  nodes: [
    {
      id: 'container',
      caseId: 'container',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '集装箱',
      summary: '用标准铁箱重写全球物流接口。',
      detail:
        '集装箱之前，货物以散件、麻袋、木箱等方式运输，装卸依赖大量码头工人，效率低、损耗高、偷盗多。集装箱把货物封装成标准单元，让起重机、船舶、卡车和铁路可以按同一套尺寸与固定点操作。',
      boundary: '集装箱不是全球化的唯一原因；贸易政策、通信、金融与产业链变化同样重要。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['levinson-box'],
    },
    {
      id: 'container-standard',
      caseId: 'container',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '标准接口：让不同系统能交接',
      summary: '统一尺寸、角件与吊装方式，使货柜可以在船、港、卡车、铁路之间直接转换。',
      detail:
        '标准化的价值不在于箱子本身，而在于它把“交接”从每次重新谈判，变成可预期的流程。码头工人不必逐件搬运，承运人不必为每种货物设计不同装载方式，保险公司也更容易评估损耗。接口一旦统一，专用设备投资才变得划算。',
      boundary: '标准需要足够多的参与方同时采用，并且要保留升级与兼容机制；过早锁定错误标准会带来长期成本。',
      counter: '对于超大件、散装液体或冷链等特殊货物，标准化集装箱并非最优解。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['levinson-box'],
    },
    {
      id: 'container-throughput',
      caseId: 'container',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '吞吐量：固定投资被更多货物摊薄',
      summary: '专用码头、起重机和更大的船，需要高吞吐量才能回收成本。',
      detail:
        '集装箱码头需要岸桥、堆场、集卡和信息系统等固定投资。货物越集中，单箱装卸成本越低，船公司也越愿意部署更大的船。规模经济与网络密度互相强化，把小港口推向边缘。',
      boundary: '吞吐量受腹地经济、铁路连接、航道水深和工会/监管影响；不是所有港口都能成为枢纽。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['levinson-box', 'world-bank-logistics'],
    },
    {
      id: 'container-network',
      caseId: 'container',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '互补网络：港口、铁路与船公司',
      summary: '只有当码头、集卡、铁路和航线一起投资，集装箱的优势才会显现。',
      detail:
        '集装箱不是孤立技术：没有足够的岸桥、堆场和内陆运输，箱子会在港口堆积；没有稳定航线，船公司不敢造大船。参与方需要协调投资，先有鸡还是先有蛋的问题，往往靠港口当局、大货主或政府推动解决。',
      boundary: '互补投资需要长期协调；如果只建码头不建内陆连接，投资回报会很差。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['levinson-box', 'world-bank-logistics'],
    },
    {
      id: 'container-labor',
      caseId: 'container',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '转换成本：码头工人的抵抗',
      summary: '效率提升的同时，原有装卸岗位与工会权力被压缩，转型并非无痛。',
      detail:
        '集装箱化大幅减少码头装卸所需人力，工会通过罢工、谈判与岗位保留条款争取过渡安排。技术扩散的速度，不只取决于工程可行性，也取决于谁承担转型成本、谁获得效率收益。',
      boundary: '不同国家、港口的劳工制度差异很大；不能把美国经验直接套用于所有港口。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['levinson-box'],
    },
    {
      id: 'container-condition',
      caseId: 'container',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：贸易增长与基础设施投资',
      summary: '战后贸易扩张、港口投资、公路与铁路网络，为集装箱提供了需求与互补设施。',
      detail:
        '如果贸易量不足、内陆交通不连通、港口水深不够，集装箱的效率优势无法兑现。标准、需求与互补基础设施必须同时到位，技术才会从“可行”变成“主导”。',
      boundary: '贸易保护主义、航道中断或港口拥堵会削弱集装箱网络的优势。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['levinson-box', 'world-bank-logistics'],
    },
    {
      id: 'container-outcome',
      caseId: 'container',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：全球供应链与港口兴衰',
      summary: '运输成本下降，生产可以跨更远距离分工，港口城市重新排序。',
      detail:
        '更低、更可预测的运输成本，让企业把生产环节放到劳动力、土地或资源更便宜的地方，同时保持最终市场可达。港口从劳动密集的装卸中心，变成资本密集的物流枢纽；一些旧港口衰落，一些新枢纽崛起。',
      boundary: '运输成本只是全球化的一个变量；关税、通信、金融、制度与劳动力成本同样关键。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['levinson-box', 'world-bank-logistics'],
    },
    {
      id: 'container-risk',
      caseId: 'container',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：效率网络也有单点',
      summary: '高度优化的网络减少冗余，一条航道或一个枢纽的中断会迅速传导。',
      detail:
        '集装箱网络追求规模与准时，港口、运河、铁路枢纽成为关键节点。劳资纠纷、天气、地缘冲突或设备故障都可能造成连锁拥堵。效率与韧性之间需要权衡，而冗余在平静时期看起来像浪费。',
      boundary: '数字化与多式联运可以提高可见性，但不能消除物理瓶颈。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['world-bank-logistics'],
    },
    {
      id: 'container-opp',
      caseId: 'container',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：找到你行业里的“角件”',
      summary: '把非标服务拆成可交接的标准单元，往往比再做一个平台更有价值。',
      detail:
        '每个行业都有交接摩擦：报价、验收、交付、结算、数据格式。谁能定义一套低摩擦接口，并让互补方愿意采用，谁就能获得网络效应。对个人，先在一个细分场景里把“交付物”标准化，再把它变成可复制的服务。',
      boundary: '标准需要治理与采用者；只做一份模板，不等于建立了标准。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['levinson-box'],
    },
  ],
  edges: [
    {
      id: 'container-e1',
      caseId: 'container',
      from: 'container',
      to: 'container-standard',
      label: '统一规格',
      explanation: '集装箱把货物封装成标准单元，使不同运输方式可以按同一接口交接。',
      tone: 'cause',
    },
    {
      id: 'container-e2',
      caseId: 'container',
      from: 'container',
      to: 'container-throughput',
      label: '专用设备',
      explanation: '标准箱推动岸桥、堆场和专用船舶投资，吞吐量成为成本关键。',
      tone: 'cause',
    },
    {
      id: 'container-e3',
      caseId: 'container',
      from: 'container-standard',
      to: 'container-network',
      label: '标准吸引互补投资',
      explanation: '尺寸与吊装方式统一后，港口、卡车、铁路才愿意投入专用设备。',
      tone: 'enable',
    },
    {
      id: 'container-e4',
      caseId: 'container',
      from: 'container-throughput',
      to: 'container-network',
      label: '密度吸引航线',
      explanation: '高吞吐量让船公司愿意增加航线与更大的船，进一步提升港口密度。',
      tone: 'cause',
    },
    {
      id: 'container-e5',
      caseId: 'container',
      from: 'container-standard',
      to: 'container-labor',
      label: '效率替代人力',
      explanation: '标准箱与机械装卸减少码头人力的必要量，引发工会与岗位安排的冲突。',
      tone: 'cause',
    },
    {
      id: 'container-e6',
      caseId: 'container',
      from: 'container-network',
      to: 'container-outcome',
      label: '运输成本下降',
      explanation: '网络越密，等待与转运成本越低，远距离运输变得更可预测。',
      tone: 'cause',
    },
    {
      id: 'container-e7',
      caseId: 'container',
      from: 'container-throughput',
      to: 'container-outcome',
      label: '规模经济',
      explanation: '大船与高吞吐码头摊薄单箱成本，推动贸易量进一步集中。',
      tone: 'cause',
    },
    {
      id: 'container-e8',
      caseId: 'container',
      from: 'container-condition',
      to: 'container-outcome',
      label: '贸易与投资前提',
      explanation: '贸易增长、港口与内陆连接投资，让标准化的效率优势得以兑现。',
      tone: 'enable',
    },
    {
      id: 'container-e9',
      caseId: 'container',
      from: 'container-labor',
      to: 'container-outcome',
      label: '转型成本',
      explanation: '旧岗位被压缩，工会谈判与过渡安排影响了技术扩散的速度与方式。',
      tone: 'risk',
    },
    {
      id: 'container-e10',
      caseId: 'container',
      from: 'container-outcome',
      to: 'container-risk',
      label: '效率减少冗余',
      explanation: '网络越优化，关键港口与航道的单点中断越容易传导。',
      tone: 'risk',
    },
    {
      id: 'container-e11',
      caseId: 'container',
      from: 'container-risk',
      to: 'container-opp',
      label: '接口机会',
      explanation: '在脆弱的交接处定义标准，可以同时降低摩擦与提高可见性。',
      tone: 'enable',
    },
  ],
};

